import BaseService from '../../../base/base_service';
import _ from '../../../utils/underscore';
import { IHttpParams } from '../../../utils/xhr';
import IUserService, {
  UserServiceInitParams,
  ITokenResponse,
  IValidateGeolocationResponse
} from './user_service_interface';
import {ICashierWidgetsEligibilityDetails} from "../../../../../common/interfaces/cashier-widgets-eligibility-details.interface";
import { Check } from '@sparkware/app-utils';
import OptionsComponentController from '../../../components/options/options_component_controller';

class UserService extends BaseService implements IUserService{
  static CLASS: string = 'UserService';

  private _initParams: UserServiceInitParams = null;
  private _firebaseLoginToken: string = '';

  protected init(params: UserServiceInitParams): void {
    super.init(params);
    this._initParams = {
      tokenUrl: params.tokenUrl ? params.tokenUrl : '/cgpapi/user/gettoken',
      realityCheckDataUrl: params.realityCheckDataUrl,
      validateGeoLocationUrl: params.validateGeoLocationUrl,
      updateRealityCheckDataUrl: params.updateRealityCheckDataUrl
    };
  }

  private _oAuth2Service(): any { // need interface for OAuth2Service
    return this.serviceFactory.oAuth2Service();
  }

  public validateGeoLocation(geoLocationEncryptedResponse: string, isPeriodicFlow: boolean, errorCode: number =-1, errorMsg: string =''): Promise<IValidateGeolocationResponse> {
    const requestParams: IHttpParams = {
      geoLocationEncryptedResponse: geoLocationEncryptedResponse,
      isPeriodicFlow: isPeriodicFlow.toString(),
      errorCode: errorCode.toString(),
      errorMsg: errorMsg
    };
    return this.serviceFactory.http2Service().post(this._initParams.validateGeoLocationUrl, requestParams);
  }

  private _getHistory(): any {
    return this._oAuth2Service().extractTokenProperty('historyData');
  }

  private _getB2C(): any {
    return this._oAuth2Service().extractTokenProperty('b2c');
  }

  public getCID(): string {
    // Get token from local storage.
    const accountData = this.getAccountData();
    return accountData && accountData.Cid;
  }

  public getTaxID(): string {
    const accountData = this.getAccountData();
    return accountData && accountData.TaxId;
  }

  public getUsername(): string {
    return this._oAuth2Service().extractTokenProperty('userName');
  }

  public getAccountData(): any {
    return this.serviceFactory.oAuth2Service().getAccountData();
  }

  public getB2CData(): any {
    const b2c = this._getB2C();

    if (!b2c) {
      return null;
    }

    const parseB2CData = JSON.parse(b2c);

    return parseB2CData.message;
  }

  public getHistoryToken(): any {
    const history = this._getHistory();

    if (!history) {
      return null;
    }
    return JSON.parse(history);
  }

  public getPlatformTokenFromLogin(): string {
    return this._extractTokenPropertyByName('login', 'Token');
  }

  public getUserFirstName(): string {
    return this._extractTokenPropertyByName('accountData', 'FirstName');
  }

  public getUserLastName(): string {
    return this._extractTokenPropertyByName('accountData', 'LastName');
  }

  public getPlatformToken(params?:IHttpParams):Promise<ITokenResponse>{
    return this.serviceFactory.http2Service().post(this._initParams.tokenUrl, params)
      .then((response: ITokenResponse) => {
        return response;
      });
  }

  public renewLoginToken(params?: IHttpParams): Promise<ITokenResponse> {
    return this.getPlatformToken(params).then( (response:ITokenResponse) => {
      this._oAuth2Service().updateTokenData('login', {Token: response.token});
      return response;
    })
  }

  public updateTokenData(propertyName: string, propertyValue: any): void {
    this._oAuth2Service().updateTokenData(propertyName, propertyValue);
  }

  private _extractTokenPropertyByName(properyToExtract: any, propertyToReturn: any): any {
    const loginData = this._oAuth2Service().extractTokenProperty(properyToExtract);

    if (!loginData) {
      return null;
    }

    const parseLoginData = JSON.parse(loginData);

    return parseLoginData[propertyToReturn];
  }

  public countPlayerChallenges(): number {
    const accountData = this.getAccountData();
    const numberOfChallenges = accountData && accountData.NumberOfChallenges;
    if(_.isNullOrUndefined(numberOfChallenges)) {
      return 0;
    }
    return numberOfChallenges;
  }

  public getRealityCheckData(): Promise<any> {
    if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
      return Promise.resolve();
    }

    return this.serviceFactory.http2Service().post(this._initParams.realityCheckDataUrl);
  }

  public async updateRealityCheckData(realityCheckIntevalMin: number): Promise<void> {
    if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
      return Promise.reject();
    }

    // Stop reminders that use the setting from localStorage
    OptionsComponentController.create().load('ReminderSettings').set(realityCheckIntevalMin);
    // Also set the reminder interval on 0 in account details and control center
    if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
      try {
        await this.serviceFactory.http2Service().post(this._initParams.updateRealityCheckDataUrl, { realityCheckIntevalMin });
      } catch (error) {
        this.logger.error("Could not call update reality check to update the interval. url: " + this._initParams.updateRealityCheckDataUrl, error);
      }
      // when the call is done, update the account details for the current session
      const accountData = this.serviceFactory.userService().getAccountData();
      if (accountData && !Check.isNullOrUndefined(accountData.RealityCheckDataIntervalInMins)) {
        accountData.RealityCheckDataIntervalInMins = realityCheckIntevalMin;
        this.updateTokenData("accountData", accountData);
      }
    }
    return Promise.resolve();
  }

  public getDepositWidgetEligibilityDetails(): ICashierWidgetsEligibilityDetails {
    if(this.serviceFactory.oAuth2Service().isAuthorized()) {
      return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('depositWidgetEligibilityDetails'), null);
    } else {
      return null;
    }
  }

  public getCashoutWidgetEligibilityDetails(): ICashierWidgetsEligibilityDetails {
    if(this.serviceFactory.oAuth2Service().isAuthorized()) {
      return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('cashoutWidgetEligibilityDetails'), null);
    } else {
      return null;
    }
  }

  public geoLocationFromLogin(): any {

    if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
      return false;
    }

    return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('geoLocation'), {});
  }

  public clearGeoLocationFromLogin(): void {
    this.updateTokenData('geoLocation', null);
  }

  public setFirebaseLoginToken(token: string): void{
    this._firebaseLoginToken = token;
  }

  public getFirebaseLoginToken(): string{
    return this._firebaseLoginToken;
  }
}

export default UserService;
